import React, { Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";

import {
  SidebarProvider,
  SidebarInset,
  readSidebarStateCookie,
} from "components/common/Sidebar/Sidebar";
import {
  sidebarShellChromeClassName,
  sidebarShellContentPanelClassName,
} from "components/layout/shell/sidebarShell";
import { AppUpdateBanner } from "components/common/AppUpdateBanner";
import { AppHeader } from "components/layout/shell/AppHeader";
import { PersonalSettingsSidebar } from "components/layout/shell/AppSidebar";
import { GlobalNavigationSearchProvider } from "components/layout/shell/GlobalNavigationSearch";
import RouteFallback from "routes/RouteFallback";
import { PATH_DASHBOARD } from "config";
import { useSessionUser } from "hooks/useSession";
import { cn } from "utils/cn";

/**
 * Shell for personal settings pages (/settings/*). Unlike ProjectLayout it does
 * not depend on projects or mount project-scoped providers - these pages are
 * workspace-independent.
 */
export const SettingsShellLayout = () => {
  const { data: sessionUser, isLoading: isSessionLoading } = useSessionUser();

  if (!sessionUser || isSessionLoading) {
    return <RouteFallback containerClassName="h-full min-h-[calc(100svh-54px)]" />;
  }

  if (!sessionUser.is_active) {
    return <Navigate to={PATH_DASHBOARD.activateAccount} replace />;
  }

  return (
    <SidebarProvider defaultOpen={readSidebarStateCookie() ?? true}>
      <GlobalNavigationSearchProvider>
        <PersonalSettingsSidebar />
        <SidebarInset className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className={sidebarShellChromeClassName}>
            <AppUpdateBanner />
            <AppHeader />
            <main className={cn(sidebarShellContentPanelClassName, "overflow-auto")}>
              <Suspense
                fallback={<RouteFallback containerClassName="h-full min-h-[calc(100svh-54px)]" />}
              >
                <Outlet />
              </Suspense>
            </main>
          </div>
        </SidebarInset>
      </GlobalNavigationSearchProvider>
    </SidebarProvider>
  );
};
